import React from 'react';
import { graphql, PageProps } from 'gatsby';
import { css } from '@emotion/react';

import Container from '../components/atoms/Container';
import Layout from '../components/Layout';
import FranchiseReviewsSlider, { FranchiseReviewsSlide } from '../components/sections/FranchiseReviewsSlider';
import SplitHero from '../components/blocks/hero/split-hero';
import paddingDefault from '../components/utils/paddingDefault';
import ServicesWeProvide from '../components/blocks/ServicesWeProvide';
import { colors } from '../components/primitives/colors';
import RichText from '../components/primitives/richtext';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import { breakpoints } from '../components/primitives/tokens';
import { PageTemplate } from '../utils/match-page-template';

interface HeroContentProps {
    children?: React.ReactNode;
}

interface UnderHeroSectionProps {
    content: string;
    headline: string;
    subheading: string;
}

const styles = {
    heroContentOuterWrap: css({
        marginTop: '20px',
        color: colors.grey800,
    }),
    heroContentInnerWrap: css({
        marginBottom: '20px',
    }),
    heroContentHeading: css({
        marginBottom: '8px',
        fontSize: '20px',
        lineHeight: '28px',
        color: colors.primaryOrange,
    }),
    background: css({
        backgroundColor: colors.grey100,
    }),
    copy: css({
        fontSize: '20px',
        lineHeight: '28px',
        [breakpoints.tb]: {
            fontSize: '18px',
        },
    }),
    underHeroSectionSubheading: css({
        marginBottom: '8px',
        fontSize: '20px',
        lineHeight: '28px',
        color: colors.primaryOrange,
    }),
    franchisePageHeading: css({
        marginBottom: '24px',
        fontSize: '36px',
        lineHeight: 1.25,
        [breakpoints.mb]: {
            marginBottom: '16px',
        },
    }),
};

const HeroContent: React.FC<HeroContentProps> = () => (
    <div css={styles.heroContentOuterWrap}>
        <div css={styles.heroContentInnerWrap}>
            <h4 css={styles.heroContentHeading}>
                Call Today
                {' '}
                <br />
                {' '}
                24/7 Emergency Services
            </h4>
        </div>
    </div>
);

const UnderHeroSection: React.FC<UnderHeroSectionProps> = ({ content, headline, subheading }) => (
    <div css={[paddingDefault(), styles.background]}>
        <Container>
            <h2 css={styles.franchisePageHeading}>{headline}</h2>
            <h4 css={styles.underHeroSectionSubheading}>{subheading}</h4>
            <RichText css={styles.copy} maxLength={4}>
                {content}
            </RichText>
        </Container>
    </div>
);

export type LocalCityPageData = Queries.LocalCityTemplateQuery;

export interface LocalCityPageContext {
    franchiseNumber: number
    id: string
    templateName: PageTemplate.HyperLocal
}

const LocalCity: React.FC<PageProps<LocalCityPageData, LocalCityPageContext>> = ({ data }) => {
    const {
        content,
        state,
        city,
    } = data?.targetCity || {};

    if (!city || typeof city !== 'string') return null;

    if (!content) return null;

    const { cmsMetroStateSections } = data;

    if (!cmsMetroStateSections) return null;

    const { image: heroImage } = cmsMetroStateSections;

    const {
        allFranchiseTestimonial: { nodes: testimonials },
        franchise,
    } = data;

    const slides: FranchiseReviewsSlide[] = [];

    for (let i = 0; i < Math.min(testimonials.length, 6); i += 1) {
        const focused = testimonials[i];
        if (
            focused.name
            && focused.review
            && focused.city
            && focused.state
            && focused.category
        ) {
            slides.push({
                name: focused.name || '',
                review: focused.review || '',
                city: focused.city || '',
                state: focused.state || '',
                category: focused.category || '',
            });
        }
    }

    const localCtaData = {
        geo: {
            city: city?.trim() || '',
            stateShort: state?.trim() || '',
        },
        franchise,
    };

    if (!franchise) return null;

    return (
        <CtaProvider
            hideNationalCallCenter={!localCtaData?.franchise?.tempOutOfService}
            hideFranchiseLink={!!localCtaData?.franchise?.tempOutOfService}
        >
            <Layout>
                <Seo
                    title={`${city} Fire and Water Damage Restoration and Cleaning Services`}
                    description={`SERVPRO provides ${city} with a fast response for home and business cleaning and restoration including water damage cleanup, fire damage and mold.`}
                    imageUrl={heroImage?.url}
                />
                <SplitHero
                    heading={`${city} Cleanup and Restoration`}
                    image={heroImage}
                    subheading="Commercial and Residential Damage Cleanup and Repair"
                    has_location_widget={!localCtaData?.franchise?.tempOutOfService}
                >
                    <HeroContent />
                </SplitHero>
                <UnderHeroSection
                    headline={`When Water, Fire, and Storm Damage Happens in ${city}, We're Ready.`}
                    subheading={`Whether you're looking for restoration, cleaning or construction services in ${city}, we're here to help 24/7.`}
                    content={content}
                />
                <ServicesWeProvide
                    data={{ title: 'Services We Provide' }}
                    visitSiteCtaHref={franchise.permalink}
                />
                <FranchiseReviewsSlider
                    data={{
                        heading: 'Customer Reviews',
                        slides,
                        city: city || '',
                    }}
                />
            </Layout>
        </CtaProvider>
    );
};

export default LocalCity;

export const query = graphql`
  query LocalCityTemplate($franchiseNumber: Int, $id: String) {
    franchise(franchiseNumber: { eq: $franchiseNumber }) {
      ...FranchiseMicrositeData
    }
    targetCity(id: { eq: $id }) {
      franchiseNumber
      countryLong
      country
      content
      city
      state
      stateLong
      franchises {
        filteredServices {
          serviceType {
            title
            sort
            franchise_service_mapping
            link {
              href
              title
            }
            icon {
              title
              url
            }
            publish_details {
              locale
            }
          }
          franchiseLink
        }
        permalink
      }
    }
    allFranchiseTestimonial(filter: { franchiseNumber: { eq: $franchiseNumber }}) {
      nodes {
        ...AllFranchiseTestimonialData
      }
    }
    cmsMetroStateSections {
      image {
        filename
        title
        url
        description
        contentStackAsset(width: 720)
      }
    }
  }
`;
